import { AlertTriangle, CreditCard, Loader2, Sparkles, Wallet, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { PaymentMethod, PaymentMethodKind } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { QueryError } from "@/components/query-error";
import { showApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

import {
  useShopPaymentMethodsQuery,
  useTogglePaymentMethodMutation,
} from "./api";

const KNOWN_KINDS = new Set<string>(["CreditCard", "Wallet", "Mock"]);

const ICONS: Record<PaymentMethodKind, LucideIcon> = {
  CreditCard: CreditCard,
  Wallet: Wallet,
  Mock: Wrench,
};

interface Props {
  shopSlug: string;
  disabled?: boolean;
}

/**
 * Shop ayarları altında "Ödeme Yöntemleri" sekmesi.
 *
 * Her method için tek toggle: aktif/pasif. Method altında platform-bazlı
 * concrete provider seçimi MVP'de yok (1 method = 1 provider, varsayılan
 * Param/Wallet/Dev). v1.5'te birden fazla provider olunca 2. seviye seçim
 * eklenir.
 *
 * Wallet "yakında" durumu: backend isAvailable=false döner, UI toggle disabled
 * + "Yakında" rozeti gösterir. Mock yöntemi prod'da hiç dönmez (env guard
 * backend'de).
 */
export function PaymentMethodsSection({ shopSlug, disabled }: Props) {
  const query = useShopPaymentMethodsQuery(shopSlug);
  const toggle = useTogglePaymentMethodMutation(shopSlug);
  const t = useT("paymentMethods");

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("loading")}
      </div>
    );
  }

  if (query.error || !query.data) {
    return (
      <QueryError
        message={t("loadError")}
        onRetry={() => query.refetch()}
        retrying={query.isFetching}
      />
    );
  }

  const methods = query.data.methods;
  const noneActive = !methods.some((m) => m.isActive && m.isAvailable);
  const noneActiveWarning = noneActive ? (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{t("noneActiveWarning")}</span>
    </div>
  ) : null;

  if (methods.length === 0) {
    return (
      <div className="space-y-3">
        {noneActiveWarning}
        <p className="py-3 text-sm text-muted-foreground">
          {t("empty")}
        </p>
      </div>
    );
  }

  async function handleToggle(method: PaymentMethod, nextActive: boolean) {
    try {
      await toggle.mutateAsync({ kind: method.kind, isActive: nextActive });
    } catch (err) {
      showApiError(err, t("updateFailed"));
    }
  }

  return (
    <div className="space-y-3">
      {noneActiveWarning}
      <ul className="divide-y rounded-md border">
        {methods.map((method) => {
          const Icon = ICONS[method.kind] ?? Sparkles;
          // "Yakında" — platform tarafında henüz aktive edilemeyen yöntemler.
          const comingSoon = !method.isAvailable;
          const isMock = method.kind === "Mock";
          const switchDisabled =
            disabled || comingSoon || toggle.isPending;
          const known = KNOWN_KINDS.has(method.kind);
          const name = known ? t(`kinds.${method.kind}.name`) : method.displayName;
          const description = known ? t(`kinds.${method.kind}.description`) : method.description;

          return (
            <li
              key={method.kind}
              className="flex flex-wrap items-center gap-4 px-4 py-3"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{name}</span>
                  {comingSoon && <Badge variant="secondary">{t("comingSoon")}</Badge>}
                  {isMock && <Badge variant="warning">{t("dev")}</Badge>}
                  {method.isActive && !comingSoon && (
                    <Badge variant="success">{t("active")}</Badge>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
              </div>
              <Switch
                checked={method.isActive}
                onCheckedChange={(checked) => handleToggle(method, checked)}
                disabled={switchDisabled}
                aria-label={method.isActive ? t("turnOff", { name }) : t("turnOn", { name })}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
